import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Typography,
} from "antd";
import { TagOutlined } from "@ant-design/icons";
import {
  buildSku,
  CUSTOM_SKU_OPTION,
  findDuplicateSku,
  formatTodaySkuDate,
  isValidSkuCode,
  isValidSkuDate,
  isValidSkuFormat,
  normalizeComparableSku,
  normalizeInlineSku,
  normalizeSkuDateInput,
  parseSku,
  sanitizeSkuCode,
  sanitizeSkuSegment,
  SKU_SERVICE_OPTIONS,
  SKU_TYPE_OPTIONS,
} from "./skuUtils";

const { Text, Paragraph } = Typography;

const getSelectionValue = (code, options) =>
  options.some((option) => option.value === code) ? code : CUSTOM_SKU_OPTION;

const resolveSelectedCode = (selectedValue, customValue) =>
  selectedValue === CUSTOM_SKU_OPTION
    ? sanitizeSkuCode(customValue)
    : sanitizeSkuCode(selectedValue);

const getCustomFieldRules = (label) => [
  {
    required: true,
    message: `Inserisci il codice ${label.toLowerCase()}`,
  },
  {
    validator: (_, value) =>
      isValidSkuCode(value)
        ? Promise.resolve()
        : Promise.reject(
            new Error(`${label} deve avere 2-5 caratteri alfanumerici.`)
          ),
  },
];

const getGuidedFormValuesFromParsed = (parsed) => ({
  serviceSelection: getSelectionValue(parsed.serviceCode, SKU_SERVICE_OPTIONS),
  customServiceCode:
    getSelectionValue(parsed.serviceCode, SKU_SERVICE_OPTIONS) ===
    CUSTOM_SKU_OPTION
      ? parsed.serviceCode
      : "",
  moduleSegment: parsed.moduleSegment || "",
  typeSelection: getSelectionValue(parsed.typeCode, SKU_TYPE_OPTIONS),
  customTypeCode:
    getSelectionValue(parsed.typeCode, SKU_TYPE_OPTIONS) === CUSTOM_SKU_OPTION
      ? parsed.typeCode
      : "",
  dateSuffix: parsed.dateSuffix,
});

const getInitialWizardState = (value, moduleName) => {
  const parsed = parseSku(value);
  const baseValues = {
    serviceSelection: undefined,
    customServiceCode: "",
    moduleSegment: sanitizeSkuSegment(moduleName),
    typeSelection: undefined,
    customTypeCode: "",
    dateSuffix: formatTodaySkuDate(),
  };

  if (parsed.isValid) {
    return {
      mode: "guided",
      manualSku: parsed.normalized,
      formValues: getGuidedFormValuesFromParsed(parsed),
      hasLegacyValue: false,
    };
  }

  return {
    mode: value ? "manual" : "guided",
    manualSku: normalizeComparableSku(value),
    formValues: baseValues,
    hasLegacyValue: Boolean(value),
  };
};

const SkuWizard = ({
  value,
  moduleName,
  existingModules,
  currentModuleId,
  onApply,
}) => {
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState("guided");
  const [manualSku, setManualSku] = useState("");
  const [hasLegacyValue, setHasLegacyValue] = useState(false);
  const [form] = Form.useForm();

  const serviceSelection = Form.useWatch("serviceSelection", form);
  const customServiceCode = Form.useWatch("customServiceCode", form);
  const moduleSegment = Form.useWatch("moduleSegment", form);
  const typeSelection = Form.useWatch("typeSelection", form);
  const customTypeCode = Form.useWatch("customTypeCode", form);
  const dateSuffix = Form.useWatch("dateSuffix", form);

  const guidedSku = useMemo(
    () =>
      buildSku({
        serviceCode: resolveSelectedCode(serviceSelection, customServiceCode),
        moduleSegment,
        typeCode: resolveSelectedCode(typeSelection, customTypeCode),
        dateSuffix,
      }),
    [
      customServiceCode,
      customTypeCode,
      dateSuffix,
      moduleSegment,
      serviceSelection,
      typeSelection,
    ]
  );

  const guidedDuplicate = useMemo(
    () => findDuplicateSku(guidedSku, existingModules, currentModuleId),
    [currentModuleId, existingModules, guidedSku]
  );

  const manualDuplicate = useMemo(
    () => findDuplicateSku(manualSku, existingModules, currentModuleId),
    [currentModuleId, existingModules, manualSku]
  );

  const manualIsValid = Boolean(manualSku) && isValidSkuFormat(manualSku);
  const canApply = mode === "guided"
    ? Boolean(guidedSku) && !guidedDuplicate
    : manualIsValid && !manualDuplicate;

  const openWizard = () => {
    const nextState = getInitialWizardState(value, moduleName);

    form.setFieldsValue(nextState.formValues);
    setManualSku(nextState.manualSku);
    setMode(nextState.mode);
    setHasLegacyValue(nextState.hasLegacyValue);
    setVisible(true);
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) {
      return;
    }

    if (nextMode === "manual") {
      setManualSku(guidedSku || manualSku);
      setMode(nextMode);
      return;
    }

    const parsedManualSku = parseSku(manualSku);

    if (parsedManualSku.isValid) {
      form.setFieldsValue(getGuidedFormValuesFromParsed(parsedManualSku));
      setHasLegacyValue(false);
    }

    setMode(nextMode);
  };

  const closeWizard = () => {
    setVisible(false);
    setManualSku("");
    setMode("guided");
    setHasLegacyValue(false);
    form.resetFields();
  };

  const handleApply = async () => {
    if (mode === "guided") {
      try {
        await form.validateFields();
      } catch (error) {
        return;
      }

      if (!guidedSku || guidedDuplicate) {
        return;
      }

      onApply(guidedSku);
      closeWizard();
      return;
    }

    if (!manualIsValid || manualDuplicate) {
      return;
    }

    onApply(manualSku);
    closeWizard();
  };

  const manualValidationMessage = !manualSku
    ? "Inserisci una SKU nel formato SERVICE[-MODULE]-TYPE-YY-MM-DD."
    : manualDuplicate
    ? `SKU già usata da ${manualDuplicate.name}.`
    : !manualIsValid
    ? "Formato non valido. Usa SERVICE[-MODULE]-TYPE-YY-MM-DD."
    : null;
  const manualValidationStatus =
    manualSku && manualValidationMessage ? "error" : undefined;

  return (
    <>
      <Button
        type="primary"
        size="small"
        icon={<TagOutlined />}
        onClick={openWizard}
      >
        Genera SKU
      </Button>
      <Modal
        title={
          <Space direction="vertical" size={2}>
            <Text strong>Genera SKU</Text>
            <Text type="secondary">
              Formato previsto: SERVICE[-MODULE]-TYPE-YY-MM-DD
            </Text>
          </Space>
        }
        open={visible}
        onOk={handleApply}
        onCancel={closeWizard}
        okText="Applica SKU"
        cancelText="Annulla"
        okButtonProps={{ disabled: !canApply }}
        destroyOnClose
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Usa il flusso guidato per generare una SKU coerente. Passa a manuale
            solo per casi speciali o per correggere valori legacy.
          </Paragraph>

          {hasLegacyValue && mode === "manual" && (
            <Alert
              type="warning"
              showIcon
              message="SKU attuale non riconosciuta"
              description="Il valore corrente non segue il formato standard. Puoi correggerlo in modalità manuale."
            />
          )}

          <Segmented
            block
            value={mode}
            options={[
              { label: "Guidato", value: "guided" },
              { label: "Manuale", value: "manual" },
            ]}
            onChange={handleModeChange}
          />

          <Form form={form} layout="vertical">
            {mode === "guided" ? (
              <>
              <Form.Item
                name="serviceSelection"
                label="Servizio"
                rules={[{ required: true, message: "Seleziona il servizio." }]}
              >
                <Select
                  placeholder="Seleziona il servizio"
                  options={SKU_SERVICE_OPTIONS}
                  onChange={(nextValue) => {
                    if (nextValue !== CUSTOM_SKU_OPTION) {
                      form.setFieldValue("customServiceCode", "");
                    }
                  }}
                />
              </Form.Item>

              {serviceSelection === CUSTOM_SKU_OPTION && (
                <Form.Item
                  name="customServiceCode"
                  label="Codice servizio"
                  rules={getCustomFieldRules("Codice servizio")}
                >
                  <Input
                    maxLength={5}
                    placeholder="Es. ADDET"
                    onChange={(event) =>
                      form.setFieldValue(
                        "customServiceCode",
                        sanitizeSkuCode(event.target.value)
                      )
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                name="moduleSegment"
                label="Codice modulo"
                extra="Prefill dal nome modulo, ma modificabile se serve."
              >
                <Input
                  placeholder="Es. FUNNEL-PLUS"
                  onChange={(event) =>
                    form.setFieldValue(
                      "moduleSegment",
                      sanitizeSkuSegment(event.target.value)
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                name="typeSelection"
                label="Tipologia"
                rules={[{ required: true, message: "Seleziona la tipologia." }]}
              >
                <Select
                  placeholder="Seleziona la tipologia"
                  options={SKU_TYPE_OPTIONS}
                  onChange={(nextValue) => {
                    if (nextValue !== CUSTOM_SKU_OPTION) {
                      form.setFieldValue("customTypeCode", "");
                    }
                  }}
                />
              </Form.Item>

              {typeSelection === CUSTOM_SKU_OPTION && (
                <Form.Item
                  name="customTypeCode"
                  label="Codice tipologia"
                  rules={getCustomFieldRules("Codice tipologia")}
                >
                  <Input
                    maxLength={5}
                    placeholder="Es. LAB"
                    onChange={(event) =>
                      form.setFieldValue(
                        "customTypeCode",
                        sanitizeSkuCode(event.target.value)
                      )
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                name="dateSuffix"
                label="Data"
                extra="Formato AA-MM-GG. Esempio: 26-03-23."
                rules={[
                  {
                    required: true,
                    message: "Inserisci la data.",
                  },
                  {
                    validator: (_, nextValue) =>
                      isValidSkuDate(normalizeSkuDateInput(nextValue))
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error("La data deve essere valida nel formato AA-MM-GG.")
                          ),
                  },
                ]}
              >
                <Input
                  placeholder="YY-MM-DD"
                  addonAfter={
                    <Button
                      size="small"
                      type="link"
                      onClick={() =>
                        form.setFieldValue("dateSuffix", formatTodaySkuDate())
                      }
                    >
                      Oggi
                    </Button>
                  }
                  onChange={(event) =>
                    form.setFieldValue(
                      "dateSuffix",
                      normalizeSkuDateInput(event.target.value)
                    )
                  }
                />
              </Form.Item>

              <Form.Item label="Anteprima">
                <Input
                  value={guidedSku}
                  readOnly
                  placeholder="Completa servizio, tipologia e data"
                  style={{ fontFamily: "monospace" }}
                />
              </Form.Item>

              {guidedDuplicate && (
                <Alert
                  type="error"
                  showIcon
                  message={`SKU già usata da ${guidedDuplicate.name}.`}
                />
              )}
              </>
            ) : (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <Form.Item
                label="SKU manuale"
                validateStatus={manualValidationStatus}
                help={manualValidationMessage}
              >
                <Input
                  value={manualSku}
                  placeholder="SERVICE[-MODULE]-TYPE-YY-MM-DD"
                  style={{ fontFamily: "monospace" }}
                  onChange={(event) =>
                    setManualSku(normalizeInlineSku(event.target.value))
                  }
                />
              </Form.Item>
              <Text type="secondary">
                La modalità manuale resta vincolata a formato valido e SKU unica.
              </Text>
              </Space>
            )}
          </Form>
        </Space>
      </Modal>
    </>
  );
};

export default SkuWizard;
